/**
 * Relation Config Menu - Column Header Configuration
 *
 * Styled to match AFFiNE's design system.
 * Opened from the column header menu or the configure button of an
 * unconfigured relation cell.
 *
 * E013-S002: Extended to support cross-doc relations via DatabaseDiscoveryBridge.
 * E012-T018: Uses RBAC-filtered database lists for security.
 */
import { I18n } from '@affine/i18n';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { RANGE_SYNC_EXCLUDE_ATTR } from '@blocksuite/std/inline';
import type { Store } from '@blocksuite/store';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import { availableRelationColumnName } from '@streamlain/database-identity';
import { css, html } from 'lit';
import { property, state } from 'lit/decorators.js';

import {
  canWriteRelationEndpoint,
  getRelationRestrictedDatabaseTitle,
  resolveRelationEndpointAccess,
} from './access-state';
import {
  countMultiValueCells,
  resolveExistingInverseRelation,
} from './database-discovery';
import {
  type DatabasesByDoc,
  getDatabasesByDoc,
  getWritableDatabasesByDocLiveData,
  type WorkspaceDatabaseInfo,
} from './database-discovery-bridge';
import type { RelationPropertyData } from './define';
import { relationDatabaseSelector } from './relation-database-selector';
import {
  canModifyTargetDoc,
  createInverseRelationUnified,
  deleteInverseRelationUnified,
  truncateCellsUnified,
  updateColumnCardinalityUnified,
  updateInverseCardinalityUnified,
} from './unified-relation-service';

// Icons
const LinkIcon = html`<svg
  width="16"
  height="16"
  viewBox="0 0 16 16"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
>
  <path
    d="M6.5 9.5L9.5 6.5M7.5 4.5L8.25 3.75C9.49264 2.50736 11.5074 2.50736 12.75 3.75C13.9926 4.99264 13.9926 7.00736 12.75 8.25L12 9M4 7L3.25 7.75C2.00736 8.99264 2.00736 11.0074 3.25 12.25C4.49264 13.4926 6.50736 13.4926 7.75 12.25L8.5 11.5"
    stroke="currentColor"
    stroke-width="1.5"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>`;

const CheckIcon = html`<svg
  width="16"
  height="16"
  viewBox="0 0 16 16"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
>
  <path
    d="M3.5 8.5L6.5 11.5L12.5 5.5"
    stroke="currentColor"
    stroke-width="1.5"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>`;

/**
 * What one press of Save did. A save can create the backlink in the target
 * and still fail before it saves the backlink into this column.
 */
export interface RelationConfigSaveResult {
  /** Backlink this save created in the target database. */
  created?: {
    targetDocId: string;
    targetDatabaseId: string;
    backlinkId: string;
  };
  /** Backlink this column was saved with, once the save committed. */
  committedBacklinkId?: string;
}

export class RelationConfigMenu extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    .relation-config {
      padding: 8px;
      min-width: 260px;
      font-family: var(--streamlain-font-family);
    }

    .relation-config-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px 8px;
      margin-bottom: 8px;
      color: var(--streamlain-v2-text-primary);
      font-size: 14px;
      font-weight: 500;
    }

    .relation-config-header svg {
      color: var(--streamlain-v2-icon-primary);
    }

    .relation-config-section {
      margin-bottom: 12px;
    }

    .relation-config-label {
      font-size: 12px;
      font-weight: 500;
      color: var(--streamlain-v2-text-secondary);
      margin-bottom: 6px;
      padding: 0 4px;
      display: block;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .relation-config-select {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
      border-radius: 8px;
      background: var(--streamlain-v2-layer-background-primary);
      color: var(--streamlain-v2-text-primary);
      font-size: 14px;
      font-family: var(--streamlain-font-family);
      cursor: pointer;
      transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23888' d='M3 4.5L6 7.5L9 4.5'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 12px center;
    }

    .relation-config-select:hover {
      border-color: var(--streamlain-v2-brand-primary);
    }

    .relation-config-select:focus {
      outline: none;
      border-color: var(--streamlain-v2-brand-primary);
      box-shadow: 0 0 0 2px var(--streamlain-v2-brand-primary);
    }

    .relation-config-option-group {
      display: flex;
      gap: 8px;
    }

    .relation-config-option {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 10px 12px;
      border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
      border-radius: 8px;
      background: var(--streamlain-v2-layer-background-primary);
      color: var(--streamlain-v2-text-primary);
      font-size: 13px;
      font-family: var(--streamlain-font-family);
      cursor: pointer;
      transition: all 0.15s ease;
      user-select: none;
    }

    .relation-config-option:hover {
      border-color: var(--streamlain-v2-brand-primary);
      background: var(--streamlain-v2-layer-background-hoverOverlay);
    }

    .relation-config-option.selected {
      border-color: var(--streamlain-v2-brand-primary);
      background: var(--streamlain-v2-brand-primaryTint);
      color: var(--streamlain-v2-brand-primary);
      font-weight: 500;
    }

    .relation-config-option input {
      display: none;
    }

    .relation-config-toggle {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 12px;
      border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
      border-radius: 8px;
      background: var(--streamlain-v2-layer-background-primary);
      cursor: pointer;
      transition: all 0.15s ease;
      user-select: none;
    }

    .relation-config-toggle:hover {
      border-color: var(--streamlain-v2-brand-primary);
      background: var(--streamlain-v2-layer-background-hoverOverlay);
    }

    .relation-config-toggle.active {
      border-color: var(--streamlain-v2-brand-primary);
      background: var(--streamlain-v2-brand-primaryTint);
    }

    .relation-config-toggle-label {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .relation-config-toggle-title {
      font-size: 14px;
      color: var(--streamlain-v2-text-primary);
    }

    .relation-config-toggle-desc {
      font-size: 11px;
      color: var(--streamlain-v2-text-secondary);
    }

    .relation-config-switch {
      width: 36px;
      height: 20px;
      border-radius: 10px;
      background: var(--streamlain-v2-layer-insideBorder-border);
      position: relative;
      transition: background 0.2s ease;
    }

    .relation-config-switch::after {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: white;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
      transition: transform 0.2s ease;
    }

    .relation-config-toggle.active .relation-config-switch {
      background: var(--streamlain-v2-brand-primary);
    }

    .relation-config-toggle.active .relation-config-switch::after {
      transform: translateX(16px);
    }

    .relation-config-toggle.disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .relation-config-toggle.disabled:hover {
      border-color: var(--streamlain-v2-layer-insideBorder-border);
      background: var(--streamlain-v2-layer-background-primary);
    }

    .relation-config-input {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
      border-radius: 8px;
      background: var(--streamlain-v2-layer-background-primary);
      color: var(--streamlain-v2-text-primary);
      font-size: 14px;
      font-family: var(--streamlain-font-family);
      margin-top: 8px;
      box-sizing: border-box;
      transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;
    }

    .relation-config-input:focus {
      outline: none;
      border-color: var(--streamlain-v2-brand-primary);
      box-shadow: 0 0 0 2px var(--streamlain-v2-brand-primary);
    }

    .relation-config-input::placeholder {
      color: var(--streamlain-v2-text-placeholder);
    }

    .relation-config-hint {
      font-size: 11px;
      color: var(--streamlain-v2-text-secondary);
      padding: 6px 4px 0;
      line-height: 1.4;
    }

    .relation-config-hint.success {
      color: var(--streamlain-v2-status-success);
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .relation-config-hint.success svg {
      width: 12px;
      height: 12px;
    }

    .relation-config-divider {
      height: 1px;
      background: var(--streamlain-v2-layer-insideBorder-border);
      margin: 12px 0;
    }

    /* Primary CTA */
    .relation-config-save {
      width: 100%;
      padding: 10px 16px;
      background: var(--streamlain-v2-brand-primary);
      color: var(--streamlain-v2-button-pureWhiteText);
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      font-family: var(--streamlain-font-family);
      transition:
        opacity 0.15s ease,
        transform 0.1s ease;
    }

    .relation-config-save:hover:not(:disabled) {
      opacity: 0.9;
    }

    .relation-config-save:active:not(:disabled) {
      transform: scale(0.98);
    }

    .relation-config-save:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    /* E013: Search input styles */
    .relation-config-search-container {
      margin-bottom: 8px;
    }

    .relation-config-search-input {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
      border-radius: 8px;
      background: var(--streamlain-v2-layer-background-primary);
      color: var(--streamlain-v2-text-primary);
      font-size: 13px;
      font-family: var(--streamlain-font-family);
      box-sizing: border-box;
      transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;
    }

    .relation-config-search-input:focus {
      outline: none;
      border-color: var(--streamlain-v2-brand-primary);
      box-shadow: 0 0 0 2px var(--streamlain-v2-brand-primary);
    }

    .relation-config-search-input::placeholder {
      color: var(--streamlain-v2-text-placeholder);
    }

    /* E013-S002: Grouped database selector styles */
    .relation-config-db-group {
      margin-bottom: 4px;
    }

    .relation-config-db-group-header {
      font-size: 12px;
      font-weight: 500;
      color: var(--streamlain-v2-text-secondary);
      padding: 6px 8px;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .relation-config-db-group-header.current {
      color: var(--streamlain-v2-brand-primary);
    }

    .relation-config-current-badge {
      font-size: 10px;
      font-weight: 400;
      opacity: 0.7;
      padding: 1px 4px;
      border-radius: 3px;
      background: var(--streamlain-v2-layer-background-hoverOverlay);
    }

    .relation-config-db-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      border: 1px solid transparent;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.15s ease;
      margin-left: 8px;
    }

    .relation-config-db-item:hover {
      background: var(--streamlain-v2-layer-background-hoverOverlay);
      border-color: var(--streamlain-v2-layer-insideBorder-border);
    }

    .relation-config-db-item.selected {
      background: var(--streamlain-v2-brand-primaryTint);
      border-color: var(--streamlain-v2-brand-primary);
    }

    .relation-config-db-item-title {
      font-size: 13px;
      color: var(--streamlain-v2-text-primary);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .relation-config-db-list {
      max-height: 240px;
      overflow-y: auto;
      border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
      border-radius: 8px;
      padding: 4px;
    }

    .relation-config-db-list::-webkit-scrollbar {
      width: 6px;
    }

    .relation-config-db-list::-webkit-scrollbar-track {
      background: transparent;
    }

    .relation-config-db-list::-webkit-scrollbar-thumb {
      background: var(--streamlain-v2-layer-insideBorder-border);
      border-radius: 3px;
    }

    .relation-config-db-list::-webkit-scrollbar-thumb:hover {
      background: var(--streamlain-v2-icon-primary);
    }

    .relation-config-no-dbs {
      font-size: 12px;
      color: var(--streamlain-v2-text-secondary);
      padding: 12px;
      text-align: center;
    }

    /* Dropdown Combobox Styles */
    .relation-config-dropdown {
      position: relative;
    }

    .relation-config-dropdown-trigger {
      width: 100%;
      padding: 8px 32px 8px 12px;
      border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
      border-radius: 8px;
      background: var(--streamlain-v2-layer-background-primary);
      color: var(--streamlain-v2-text-primary);
      font-size: 14px;
      font-family: var(--streamlain-font-family);
      cursor: pointer;
      transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;
      text-align: left;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      position: relative;
    }

    .relation-config-dropdown-trigger::after {
      content: '';
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 5px solid var(--streamlain-v2-icon-primary);
      transition: transform 0.15s ease;
    }

    .relation-config-dropdown-trigger.open::after {
      transform: translateY(-50%) rotate(180deg);
    }

    .relation-config-dropdown-trigger:hover {
      border-color: var(--streamlain-v2-brand-primary);
    }

    .relation-config-dropdown-trigger.placeholder {
      color: var(--streamlain-v2-text-placeholder);
    }

    /* E018-S008-T006: Locked state for configured relations */
    .relation-config-dropdown-trigger.locked {
      cursor: not-allowed;
      background: var(--streamlain-v2-layer-background-secondary);
      opacity: 0.8;
    }

    .relation-config-dropdown-trigger.locked::after {
      display: none;
    }

    .relation-config-locked-hint {
      font-size: 11px;
      color: var(--streamlain-v2-text-secondary);
      padding: 4px 4px 0;
      font-style: italic;
    }

    .relation-config-dropdown-panel {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      margin-top: 4px;
      border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
      border-radius: 8px;
      background: var(--streamlain-v2-layer-background-primary);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 100;
      overflow: hidden;
    }

    .relation-config-dropdown-search {
      padding: 8px;
      border-bottom: 1px solid var(--streamlain-v2-layer-insideBorder-border);
    }

    .relation-config-dropdown-search input {
      width: 100%;
      padding: 6px 10px;
      border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
      border-radius: 6px;
      background: var(--streamlain-v2-layer-background-secondary);
      color: var(--streamlain-v2-text-primary);
      font-size: 13px;
      font-family: var(--streamlain-font-family);
      box-sizing: border-box;
    }

    .relation-config-dropdown-search input:focus {
      outline: none;
      border-color: var(--streamlain-v2-brand-primary);
    }

    .relation-config-dropdown-search input::placeholder {
      color: var(--streamlain-v2-text-placeholder);
    }

    .relation-config-dropdown-list {
      max-height: 200px;
      overflow-y: auto;
      padding: 4px;
    }

    .relation-config-dropdown-list::-webkit-scrollbar {
      width: 6px;
    }

    .relation-config-dropdown-list::-webkit-scrollbar-track {
      background: transparent;
    }

    .relation-config-dropdown-list::-webkit-scrollbar-thumb {
      background: var(--streamlain-v2-layer-insideBorder-border);
      border-radius: 3px;
    }

    /* Delete button in config menu */
    .relation-config-delete-section {
      padding: 12px;
      border-top: 1px solid var(--streamlain-v2-layer-insideBorder-border);
    }

    .relation-config-delete-btn {
      width: 100%;
      padding: 8px 12px;
      border-radius: 8px;
      border: 1px solid var(--streamlain-v2-status-error);
      background: transparent;
      color: var(--streamlain-v2-status-error);
      cursor: pointer;
      font-size: 14px;
      transition: all 0.15s;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
    }

    .relation-config-delete-btn:hover {
      background: var(--streamlain-v2-status-error);
      color: white;
    }
  `;

  @property({ attribute: false })
  accessor store!: Store;

  @property({ attribute: false })
  accessor currentDatabaseId!: string;

  @property({ attribute: false })
  accessor propertyId!: string;

  @property({ attribute: false })
  accessor data!: RelationPropertyData;

  @property({ attribute: false })
  accessor onUpdate!: (data: Partial<RelationPropertyData>) => void;

  @property({ attribute: false })
  accessor sourceColumnName = '';

  @property({ attribute: false })
  accessor sourceColumnNames: readonly string[] = [];

  @property({ attribute: false })
  accessor onPreviewSourceColumnName: ((name: string) => void) | undefined;

  @property({ attribute: false })
  accessor onCommitSourceColumnName: ((name: string) => void) | undefined;

  @property({ attribute: false })
  accessor onClose: (() => void) | undefined;

  /** Called when Save starts, with what the save will have done. */
  @property({ attribute: false })
  accessor onSaveStarted:
    | ((result: RelationConfigSaveResult, done: Promise<void>) => void)
    | undefined;

  /** E012-T016: Callback to delete the property */
  @property({ attribute: false })
  accessor onDelete: (() => void) | undefined;

  @state()
  accessor _targetDocId = ''; // E013: Doc ID for cross-doc relations

  @state()
  accessor _targetDatabaseId = '';

  @state()
  accessor _cardinality: 'one' | 'many' = 'many';

  @state()
  accessor _inverseCardinality: 'one' | 'many' = 'many';

  // Relations are always bidirectional — the user no longer has a choice.
  // Kept as a field (not removed) to minimize churn in handleSave / sync paths.
  @state()
  accessor _bidirectional = true;

  @state()
  accessor _backlinkName =
    I18n['com.streamlain.streamlain.relation.config.default-backlink-name']();

  private _backlinkNameEdited = false;

  private _pendingSourceColumnName: string | undefined;

  private _syncedDataKey: string | undefined;

  @state()
  accessor _initialized = false;

  // E013: Subscription to bridge LiveData for reactive updates
  private _bridgeUnsubscribe: (() => void) | null = null;

  override connectedCallback() {
    super.connectedCallback();
    this._syncFromData();

    // E013: Subscribe to bridge LiveData for reactive database list updates.
    // Relation creation is bidirectional and therefore needs Doc_Update on the
    // target database doc, not just Doc_Read.
    const liveData$ = getWritableDatabasesByDocLiveData();
    if (liveData$) {
      const subscription = liveData$.subscribe(() => {
        this.requestUpdate();
      });
      this._bridgeUnsubscribe = () => subscription.unsubscribe();
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    // Cleanup subscription
    if (this._bridgeUnsubscribe) {
      this._bridgeUnsubscribe();
      this._bridgeUnsubscribe = null;
    }
  }

  override updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    if (
      this.data &&
      ['data', 'store', 'currentDatabaseId', 'propertyId'].some(key =>
        changedProperties.has(key)
      )
    ) {
      this._syncFromData();
    }
  }

  private _syncFromData() {
    if (!this.data) return;
    // Adding the backlink replaces the schema's column objects. An unchanged
    // source config can therefore arrive again while this draft is being saved.
    // Only a different column or changed persisted config should reset the draft.
    const dataKey = JSON.stringify([
      this.store?.id,
      this.currentDatabaseId,
      this.propertyId,
      this.data.targetDocId || '',
      this.data.targetDatabaseId || '',
      this.data.targetFieldId || '',
      this.data.cardinality || 'many',
      this.data.inverseFieldId || '',
      this.data.inverseCardinality || 'many',
    ]);
    if (dataKey === this._syncedDataKey) return;
    this._syncedDataKey = dataKey;
    this._targetDocId = this.data.targetDocId || ''; // E013: Sync docId
    this._targetDatabaseId = this.data.targetDatabaseId || '';
    this._cardinality = this.data.cardinality || 'many';
    this._inverseCardinality = this.data.inverseCardinality || 'many';
    // Always bidirectional — no uni-directional option anymore.
    this._bidirectional = true;
    this._backlinkNameEdited = false;
    // A duplicated relation arrives with its target but without a backlink.
    const target = this.data.inverseFieldId
      ? undefined
      : this._findDatabase(this._targetDatabaseId, this._targetDocId);
    if (target) {
      this._backlinkName = this._createDefaultBacklinkName(target);
    }

    this._initialized = true;
  }

  /**
   * E013-S002: Get current doc ID from store
   */
  private get currentDocId(): string {
    return this.store.id;
  }

  private get allDatabasesByDoc(): DatabasesByDoc[] {
    return getDatabasesByDoc();
  }

  /**
   * E013-S002: Handle database selection from grouped list
   */
  private _selectDatabase(db: WorkspaceDatabaseInfo) {
    this._targetDocId = db.docId;
    this._targetDatabaseId = db.blockId;

    if (!this.data?.targetDatabaseId && this._isSourceColumnDefaultName()) {
      const sourceName = this._createDefaultSourceColumnName(db);
      this.sourceColumnName = sourceName;
      this._pendingSourceColumnName = sourceName;
      this.onPreviewSourceColumnName?.(sourceName);
    }

    if (!this.data?.inverseFieldId && !this._backlinkNameEdited) {
      this._backlinkName = this._createDefaultBacklinkName(db);
    }
  }

  private _isSourceColumnDefaultName(): boolean {
    const name = this.sourceColumnName.trim();
    return !name || name === 'Relation';
  }

  private _createDefaultSourceColumnName(
    targetDb: WorkspaceDatabaseInfo
  ): string {
    return availableRelationColumnName(
      targetDb.databaseTitle,
      this.sourceColumnNames
    );
  }

  private _createDefaultBacklinkName(targetDb: WorkspaceDatabaseInfo): string {
    const sourceDbTitle = this._getSourceDatabaseTitle();
    return availableRelationColumnName(
      sourceDbTitle,
      targetDb.columns.map(column => column.name)
    );
  }

  private _findDatabase(
    blockId: string,
    docId: string
  ): WorkspaceDatabaseInfo | undefined {
    if (!blockId) return undefined;
    for (const group of this.allDatabasesByDoc) {
      const db = group.databases.find(
        database =>
          database.blockId === blockId && (!docId || database.docId === docId)
      );
      if (db) return db;
    }
    return undefined;
  }

  private _getSourceDatabaseTitle(): string {
    for (const group of this.allDatabasesByDoc) {
      const db = group.databases.find(
        database => database.blockId === this.currentDatabaseId
      );
      if (db?.databaseTitle.trim()) {
        return db.databaseTitle.trim();
      }
    }

    return 'Relation';
  }

  /**
   * Get the selected database title for display
   */
  private get selectedDatabaseTitle(): string | null {
    if (!this._targetDatabaseId) return null;

    const access = resolveRelationEndpointAccess({
      targetDatabaseId: this._targetDatabaseId,
      targetDocId: this._targetDocId || this.data?.targetDocId,
    });
    if (access.state === 'restricted') {
      return getRelationRestrictedDatabaseTitle();
    }
    return access.databaseTitle || null;
  }

  /**
   * E012-T015: Handle save with Cross-Doc support.
   * E018-S008-T005: Simplified with unified relation API.
   */
  private handleSave(): Promise<void> {
    const result: RelationConfigSaveResult = {};
    const done = this.runSave(result);
    this.onSaveStarted?.(result, done);
    return done;
  }

  private async runSave(result: RelationConfigSaveResult) {
    if (!this._targetDatabaseId) return;

    const targetDocId = this._targetDocId || this.currentDocId;
    const inverseTargetIsCrossDoc = targetDocId !== this.currentDocId;
    let inverseFieldId = this.data?.inverseFieldId;
    let oldInverseCardinality = this.data?.inverseCardinality || 'many';
    if (this._bidirectional && !inverseFieldId && !inverseTargetIsCrossDoc) {
      try {
        const existingInverse = resolveExistingInverseRelation(
          this.store,
          this.currentDatabaseId,
          this._targetDatabaseId,
          this.propertyId,
          this.currentDocId
        );
        if (existingInverse) {
          inverseFieldId = existingInverse.propertyId;
          oldInverseCardinality = existingInverse.cardinality;
        }
      } catch {
        await this.showConfirm(
          I18n['com.streamlain.streamlain.relation.config.save-failed-title'](),
          I18n[
            'com.streamlain.streamlain.relation.config.inverse-create-failed'
          ](),
          I18n['com.streamlain.confirmModal.button.ok'](),
          'error'
        );
        return;
      }
    }

    // Check the actual recovered backlink before any metadata change.
    const oldCardinality = this.data?.cardinality || 'many';
    const sourceChangingToOne =
      oldCardinality === 'many' && this._cardinality === 'one';
    const inverseChangingToOne =
      this._bidirectional &&
      oldInverseCardinality === 'many' &&
      this._inverseCardinality === 'one';

    if (inverseChangingToOne && inverseTargetIsCrossDoc) {
      await this.showConfirm(
        I18n['com.streamlain.streamlain.relation.config.save-failed-title'](),
        I18n[
          'com.streamlain.streamlain.relation.config.cross-doc-truncation-unsupported'
        ](),
        I18n['com.streamlain.confirmModal.button.ok'](),
        'error'
      );
      return;
    }

    // Count affected cells
    let sourceCount = 0;
    let inverseCount = 0;

    if (sourceChangingToOne) {
      sourceCount = countMultiValueCells(
        this.store,
        this.currentDatabaseId,
        this.propertyId
      );
    }

    if (inverseChangingToOne && inverseFieldId && !inverseTargetIsCrossDoc) {
      // For InDoc, we can count directly
      // For CrossDoc, we'd need a cross-doc count function (TODO)
      inverseCount = countMultiValueCells(
        this.store,
        this._targetDatabaseId,
        inverseFieldId
      );
    }

    let pendingChange: 'source' | 'inverse' | 'both' | null = null;

    // Show warning if truncation needed
    if (sourceCount > 0 || inverseCount > 0) {
      const totalAffected = sourceCount + inverseCount;
      pendingChange =
        sourceCount > 0 && inverseCount > 0
          ? 'both'
          : sourceCount > 0
            ? 'source'
            : 'inverse';

      const confirmed = await this.showConfirm(
        I18n['com.streamlain.streamlain.relation.data-loss-warning-title'](),
        I18n[
          'com.streamlain.streamlain.relation.data-loss-warning-description'
        ]({
          count: String(totalAffected),
        }),
        I18n['com.streamlain.streamlain.relation.truncate-and-save'](),
        'error'
      );

      if (!confirmed) return;
    }

    const preparedSave = await this.prepareSave(inverseFieldId, result);
    if (!preparedSave) return;

    if (pendingChange) {
      const truncated = await this.performCardinalityTruncation(
        pendingChange,
        preparedSave.inverseFieldId
      );
      if (!truncated) return;
    }

    this.commitSave(preparedSave.inverseFieldId, result);
  }

  /**
   * Perform all async save steps that can still fail before cell truncation
   * mutates local relation values.
   */
  private async prepareSave(
    inverseFieldId: string | undefined,
    result: RelationConfigSaveResult
  ): Promise<{ inverseFieldId?: string } | null> {
    const targetDocId = this._targetDocId || this.currentDocId;

    if (
      !canWriteRelationEndpoint({
        targetDatabaseId: this._targetDatabaseId,
        targetDocId,
      })
    ) {
      await this.showConfirm(
        I18n['com.streamlain.streamlain.relation.target-read-only-title'](),
        I18n[
          'com.streamlain.streamlain.relation.target-read-only-description'
        ](),
        I18n['com.streamlain.confirmModal.button.ok']()
      );
      return null;
    }

    // E018-S008-T005: Use unified API - no if/else for InDoc vs CrossDoc
    // A created inverse gets both cardinalities with its creation.
    const createsInverse = this._bidirectional && !inverseFieldId;
    if (createsInverse) {
      const created = await createInverseRelationUnified({
        store: this.store,
        sourceDocId: this.currentDocId,
        sourceDatabaseId: this.currentDatabaseId,
        targetDocId,
        targetDatabaseId: this._targetDatabaseId,
        backlinkName: this._backlinkName,
        sourceRelationId: this.propertyId,
        inverseCardinality: this._inverseCardinality,
        sourceCardinality: this._cardinality, // For symmetric stealing
      });

      if (created.success && created.inversePropertyId) {
        inverseFieldId = created.inversePropertyId;
        result.created = {
          targetDocId,
          targetDatabaseId: this._targetDatabaseId,
          backlinkId: inverseFieldId,
        };
      } else {
        const message =
          created.error?.message ??
          I18n[
            'com.streamlain.streamlain.relation.config.inverse-create-failed'
          ]();
        console.error(
          '[RelationConfig] Failed to create inverse relation:',
          message
        );
        await this.showConfirm(
          I18n['com.streamlain.streamlain.relation.config.save-failed-title'](),
          message,
          I18n['com.streamlain.confirmModal.button.ok'](),
          'error'
        );
        return null;
      }
    }

    // Sync cardinality changes to an existing inverse column for symmetric
    // stealing. A cross-document inverse created in this save is not readable
    // yet through a new open of its document.
    if (inverseFieldId && this._bidirectional && !createsInverse) {
      // 1. source.cardinality → inverse.inverseCardinality
      // When our cardinality changes, the inverse column's inverseCardinality must match
      const inverseCardinalityResult = await updateInverseCardinalityUnified({
        store: this.store,
        currentDocId: this.currentDocId,
        targetDocId,
        targetDatabaseId: this._targetDatabaseId,
        inverseFieldId,
        newInverseCardinality: this._cardinality,
      });
      if (!inverseCardinalityResult.success) {
        await this.showConfirm(
          I18n['com.streamlain.streamlain.relation.config.save-failed-title'](),
          inverseCardinalityResult.error?.message ??
            I18n[
              'com.streamlain.streamlain.relation.config.inverse-update-failed'
            ](),
          I18n['com.streamlain.confirmModal.button.ok'](),
          'error'
        );
        return null;
      }

      // 2. source.inverseCardinality → inverse.cardinality
      // When our inverseCardinality changes, the inverse column's cardinality must match
      const columnCardinalityResult = await updateColumnCardinalityUnified({
        store: this.store,
        currentDocId: this.currentDocId,
        targetDocId,
        targetDatabaseId: this._targetDatabaseId,
        inverseFieldId,
        newCardinality: this._inverseCardinality,
      });
      if (!columnCardinalityResult.success) {
        await this.showConfirm(
          I18n['com.streamlain.streamlain.relation.config.save-failed-title'](),
          columnCardinalityResult.error?.message ??
            I18n[
              'com.streamlain.streamlain.relation.config.inverse-update-failed'
            ](),
          I18n['com.streamlain.confirmModal.button.ok'](),
          'error'
        );
        return null;
      }
    }

    return { inverseFieldId };
  }

  private commitSave(
    inverseFieldId: string | undefined,
    result: RelationConfigSaveResult
  ) {
    this.onUpdate({
      targetDocId: this._targetDocId || undefined,
      targetDatabaseId: this._targetDatabaseId,
      targetFieldId: 'title',
      cardinality: this._cardinality,
      inverseFieldId,
      inverseCardinality: this._bidirectional
        ? this._inverseCardinality
        : undefined,
    });
    result.committedBacklinkId = inverseFieldId;
    if (this._pendingSourceColumnName) {
      this.onCommitSourceColumnName?.(this._pendingSourceColumnName);
      this._pendingSourceColumnName = undefined;
    }

    // Close the menu after saving
    this.onClose?.();
  }

  // ==========================================================================
  // Native ConfirmModal Bridge
  // ==========================================================================

  /**
   * Show AFFiNE's native ConfirmModal via the window bridge.
   * Returns true if user confirmed, false if cancelled.
   */
  private showConfirm(
    title: string,
    description: string,
    confirmText: string,
    variant: 'primary' | 'error' = 'primary'
  ): Promise<boolean> {
    const modal = window.__confirmModal;
    if (!modal) {
      // Without the native modal, destructive relation operations must not
      // continue silently.
      console.warn('[RelationConfig] __confirmModal bridge not available');
      return Promise.resolve(false);
    }

    return new Promise<boolean>(resolve => {
      modal.openConfirmModal({
        title,
        description,
        confirmText,
        cancelText: I18n['com.streamlain.confirmModal.button.cancel'](),
        confirmButtonOptions: { variant },
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  }

  // ==========================================================================
  // Cardinality Change Logic
  // ==========================================================================

  /**
   * Perform truncation after user confirmed cardinality change.
   */
  private async performCardinalityTruncation(
    pendingChange: 'source' | 'inverse' | 'both',
    inverseFieldId: string | undefined
  ): Promise<boolean> {
    const targetDocId = this._targetDocId || this.currentDocId;

    if (pendingChange === 'source' || pendingChange === 'both') {
      const sourceResult = await truncateCellsUnified({
        store: this.store,
        currentDocId: this.currentDocId,
        targetDocId: this.currentDocId,
        targetDatabaseId: this.currentDatabaseId,
        propertyId: this.propertyId,
        inverseCardinality: this._inverseCardinality,
        inverseDatabaseId: this._targetDatabaseId,
        inverseDocId: targetDocId,
        inverseFieldId,
      });
      if (!sourceResult.success) {
        await this.showConfirm(
          I18n['com.streamlain.streamlain.relation.config.save-failed-title'](),
          sourceResult.error?.message ??
            I18n[
              'com.streamlain.streamlain.relation.config.cardinality-update-failed'
            ](),
          I18n['com.streamlain.confirmModal.button.ok'](),
          'error'
        );
        return false;
      }
    }

    if (
      (pendingChange === 'inverse' || pendingChange === 'both') &&
      inverseFieldId
    ) {
      const inverseResult = await truncateCellsUnified({
        store: this.store,
        currentDocId: this.currentDocId,
        targetDocId,
        targetDatabaseId: this._targetDatabaseId,
        propertyId: inverseFieldId,
        inverseCardinality: this._cardinality,
        inverseDatabaseId: this.currentDatabaseId,
        inverseDocId: this.currentDocId,
        inverseFieldId: this.propertyId,
      });
      if (!inverseResult.success) {
        await this.showConfirm(
          I18n['com.streamlain.streamlain.relation.config.save-failed-title'](),
          inverseResult.error?.message ??
            I18n[
              'com.streamlain.streamlain.relation.config.cardinality-update-failed'
            ](),
          I18n['com.streamlain.confirmModal.button.ok'](),
          'error'
        );
        return false;
      }
    }

    return true;
  }

  // ==========================================================================
  // E012-T016: Delete Logic (uses native ConfirmModal)
  // ==========================================================================

  /**
   * Show delete confirmation via native AFFiNE ConfirmModal.
   */
  private async handleDeleteClick() {
    const hasBidirectional = !!this.data?.inverseFieldId;

    if (!hasBidirectional) {
      if (this.data?.targetDatabaseId) {
        await this.showConfirm(
          I18n[
            'com.streamlain.streamlain.relation.config.delete-failed-title'
          ](),
          I18n[
            'com.streamlain.streamlain.relation.config.delete-inverse-metadata-missing'
          ](),
          I18n['com.streamlain.confirmModal.button.ok'](),
          'error'
        );
        return;
      }
      this.onDelete?.();
      this.onClose?.();
      return;
    }

    const targetDocId = this.data?.targetDocId || this.currentDocId;
    const canModify = await canModifyTargetDoc(this.currentDocId, targetDocId);
    const targetDbTitle =
      this.selectedDatabaseTitle ||
      I18n[
        'com.streamlain.streamlain.relation.config.target-database-fallback'
      ]();

    if (!canModify) {
      await this.showConfirm(
        I18n['com.streamlain.streamlain.relation.config.delete-title'](),
        I18n[
          'com.streamlain.streamlain.relation.config.delete-no-target-access'
        ]({
          target: targetDbTitle,
          brand: BRAND_DISPLAY_NAME,
        }),
        I18n['com.streamlain.confirmModal.button.ok'](),
        'primary'
      );
      return;
    }

    // Has permission: Ask to delete both sides
    const deleteBoth = await this.showConfirm(
      I18n['com.streamlain.streamlain.relation.config.delete-title'](),
      I18n['com.streamlain.streamlain.relation.config.delete-both-confirm']({
        target: targetDbTitle,
      }),
      I18n['com.streamlain.streamlain.relation.config.delete-both'](),
      'error'
    );

    if (deleteBoth) {
      await this.deleteWithInverse();
    }
  }

  /**
   * Delete the relation and its inverse backlink.
   */
  private async deleteWithInverse() {
    if (this.data?.inverseFieldId) {
      const targetDocId = this.data.targetDocId || this.currentDocId;
      const result = await deleteInverseRelationUnified({
        store: this.store,
        currentDocId: this.currentDocId,
        targetDocId,
        targetDatabaseId: this.data.targetDatabaseId,
        inverseFieldId: this.data.inverseFieldId,
      });

      if (!result.success) {
        console.error(
          '[RelationConfig] Failed to delete inverse relation:',
          result.error?.message
        );
        return;
      }
    }

    this.onDelete?.();
    this.onClose?.();
  }

  override render() {
    const isValid = this._targetDatabaseId !== '';
    const hasBidirectional = !!this.data?.inverseFieldId;
    // Saving a relation without a backlink creates the backlink, so the
    // user sees which database receives the second column of the pair.
    const pairTarget = hasBidirectional ? null : this.selectedDatabaseTitle;
    // E018-S008-T006: Lock target database once configured (has saved targetDatabaseId)
    const isTargetLocked = !!this.data?.targetDatabaseId;

    // Stop propagation to prevent menu from closing
    const stopProp = (e: Event) => e.stopPropagation();

    return html`
      <div class="relation-config" @click=${stopProp} @mousedown=${stopProp}>
        <div class="relation-config-header">
          ${LinkIcon}
          <span
            >${I18n['com.streamlain.streamlain.relation.config.title']()}</span
          >
        </div>

        <!-- Target Database - Dropdown with integrated search -->
        <!-- E018-S008-T006: Locked once configured to prevent chaos -->
        <div class="relation-config-section">
          <label class="relation-config-label"
            >${I18n[
              'com.streamlain.streamlain.relation.config.target-database'
            ]()}</label
          >
          ${isTargetLocked
            ? html`<button
                  type="button"
                  class="relation-config-dropdown-trigger locked"
                  disabled
                >
                  ${this.selectedDatabaseTitle}
                </button>
                <div class="relation-config-locked-hint">
                  ${I18n[
                    'com.streamlain.streamlain.relation.config.target-locked-hint'
                  ]()}
                </div>`
            : html`<uni-lit
                .uni=${relationDatabaseSelector}
                .props=${{
                  value: this._targetDatabaseId,
                  currentDocId: this.currentDocId,
                  menuPortalContainer:
                    this.closest<HTMLElement>(`[${RANGE_SYNC_EXCLUDE_ATTR}]`) ??
                    undefined,
                  onChange: (database: WorkspaceDatabaseInfo) =>
                    this._selectDatabase(database),
                  requiredPermission: 'write',
                  placeholder:
                    I18n[
                      'com.streamlain.streamlain.relation.config.select-database'
                    ](),
                }}
              ></uni-lit>`}
        </div>

        <!-- Link Type -->
        <div class="relation-config-section">
          <label class="relation-config-label"
            >${I18n[
              'com.streamlain.streamlain.relation.config.link-type'
            ]()}</label
          >
          <div class="relation-config-option-group">
            <label
              class="relation-config-option ${this._cardinality === 'many'
                ? 'selected'
                : ''}"
              @click=${() => (this._cardinality = 'many')}
            >
              <input
                type="radio"
                name="cardinality"
                value="many"
                ?checked=${this._cardinality === 'many'}
              />
              ${I18n[
                'com.streamlain.streamlain.relation.config.cardinality-many'
              ]()}
            </label>
            <label
              class="relation-config-option ${this._cardinality === 'one'
                ? 'selected'
                : ''}"
              @click=${() => (this._cardinality = 'one')}
            >
              <input
                type="radio"
                name="cardinality"
                value="one"
                ?checked=${this._cardinality === 'one'}
              />
              ${I18n[
                'com.streamlain.streamlain.relation.config.cardinality-one'
              ]()}
            </label>
          </div>
          <div class="relation-config-hint">
            ${this._cardinality === 'many'
              ? I18n[
                  'com.streamlain.streamlain.relation.config.link-many-hint'
                ]()
              : I18n[
                  'com.streamlain.streamlain.relation.config.link-one-hint'
                ]()}
          </div>
        </div>

        <div class="relation-config-divider"></div>

        <!-- Backlink config — relations are always bidirectional -->
        <div class="relation-config-section">
          ${!hasBidirectional
            ? html`
                <label class="relation-config-label"
                  >${I18n[
                    'com.streamlain.streamlain.relation.config.backlink-name'
                  ]()}</label
                >
                <input
                  type="text"
                  class="relation-config-input"
                  placeholder=${I18n[
                    'com.streamlain.streamlain.relation.config.backlink-name'
                  ]()}
                  value=${this._backlinkName}
                  @input=${(e: Event) => {
                    this._backlinkNameEdited = true;
                    this._backlinkName = (e.target as HTMLInputElement).value;
                  }}
                  @click=${stopProp}
                  @mousedown=${stopProp}
                  @keydown=${stopProp}
                />

                <!-- Inverse Link Type -->
                <label class="relation-config-label" style="margin-top: 12px;"
                  >${I18n[
                    'com.streamlain.streamlain.relation.config.backlink-type'
                  ]()}</label
                >
                <div class="relation-config-option-group">
                  <label
                    class="relation-config-option ${this._inverseCardinality ===
                    'many'
                      ? 'selected'
                      : ''}"
                    @click=${() => (this._inverseCardinality = 'many')}
                  >
                    <input
                      type="radio"
                      name="inverseCardinality"
                      value="many"
                      ?checked=${this._inverseCardinality === 'many'}
                    />
                    ${I18n[
                      'com.streamlain.streamlain.relation.config.cardinality-many'
                    ]()}
                  </label>
                  <label
                    class="relation-config-option ${this._inverseCardinality ===
                    'one'
                      ? 'selected'
                      : ''}"
                    @click=${() => (this._inverseCardinality = 'one')}
                  >
                    <input
                      type="radio"
                      name="inverseCardinality"
                      value="one"
                      ?checked=${this._inverseCardinality === 'one'}
                    />
                    ${I18n[
                      'com.streamlain.streamlain.relation.config.cardinality-one'
                    ]()}
                  </label>
                </div>
                <div class="relation-config-hint">
                  ${this._inverseCardinality === 'many'
                    ? I18n[
                        'com.streamlain.streamlain.relation.config.backlink-many-hint'
                      ]()
                    : I18n[
                        'com.streamlain.streamlain.relation.config.backlink-one-hint'
                      ]()}
                </div>
                ${pairTarget
                  ? html`<div
                      class="relation-config-hint"
                      data-testid="relation-config-pair-hint"
                    >
                      ${I18n[
                        'com.streamlain.streamlain.relation.config.pair-hint'
                      ]({ database: pairTarget })}
                    </div>`
                  : ''}
              `
            : ''}
          ${hasBidirectional
            ? html`
                <div class="relation-config-hint success">
                  ${CheckIcon}
                  ${I18n[
                    'com.streamlain.streamlain.relation.config.bidirectional-active'
                  ]()}
                </div>
              `
            : ''}
        </div>

        <!-- Save Button -->
        <button
          class="relation-config-save"
          ?disabled=${!isValid}
          @click=${() => this.handleSave()}
        >
          ${I18n['com.streamlain.streamlain.relation.config.save']()}
        </button>

        <!-- E012-T016: Delete Button (only show if configured and onDelete provided) -->
        ${this.data?.targetDatabaseId && this.onDelete
          ? html`
              <div class="relation-config-delete-section">
                <button
                  class="relation-config-delete-btn"
                  @click=${() => this.handleDeleteClick()}
                >
                  ${I18n[
                    'com.streamlain.streamlain.relation.config.delete-column'
                  ]()}
                </button>
              </div>
            `
          : ''}
      </div>
    `;
  }
}
